<template>
  <div class="fd-page">
    <div v-if="error" class="fd-error">
      <span class="fd-error-icon">🕳️</span>
      <h1>دانستنی پیدا نشد</h1>
      <p>این مطلب حذف شده یا آدرس اشتباه است.</p>
      <router-link to="/did-you-know" class="fd-back">← بازگشت به دانستنی‌های فوتبالی</router-link>
    </div>

    <template v-else-if="fact">
      <nav class="fd-crumbs" aria-label="مسیر">
        <router-link to="/did-you-know">دانستنی‌های فوتبالی</router-link>
        <span aria-hidden="true">←</span>
        <span class="fd-crumbs-here">{{ fact.title }}</span>
      </nav>

      <div class="fd-layout">
        <!-- ستون اصلی: متن کامل مطلب -->
        <article class="fd-article">
          <header class="fd-head">
            <span class="fd-badge">💡 دانستنی فوتبالی</span>
            <span v-if="fact.category" class="fd-cat">{{ fact.category }}</span>
          </header>

          <h1 class="fd-title">{{ fact.title }}</h1>

          <div class="fd-meta">
            <span v-if="showDate" class="fd-date">📅 {{ faDate(showDate) }}</span>
            <a
                v-if="fact.source && fact.source.url"
                class="fd-source"
                :href="fact.source.url"
                target="_blank"
                rel="noopener"
            >منبع اصلی: {{ fact.source.name || 'لینک منبع' }} ↗</a>
          </div>

          <figure v-if="fact.imageUrl" class="fd-photo">
            <img
                :src="fact.imageUrl"
                :alt="fact.imageAlt || fact.title"
                loading="lazy"
            >
          </figure>

          <div
              v-if="fact.keyFigure"
              class="fd-figure"
          >
            <span class="fd-figure-num">{{ fact.keyFigure }}</span>
            <span class="fd-figure-label">{{ fact.figureLabel || 'عدد کلیدی این مطلب' }}</span>
          </div>

          <div class="fd-body">
            <p
                v-for="(p, j) in fact.paragraphs"
                :key="j"
            >{{ p }}</p>
          </div>

          <div v-if="fact.tags && fact.tags.length" class="fd-tags">
            <span
                v-for="t in fact.tags"
                :key="t"
                class="fd-tag"
            >{{ t }}</span>
          </div>

          <details v-if="fact.verifyNote" class="fd-verify">
            <summary>یادداشت ناشر</summary>
            <p>{{ fact.verifyNote }}</p>
          </details>

          <div class="fd-nav">
            <router-link to="/did-you-know" class="fd-nav-link">← همهٔ دانستنی‌ها</router-link>
            <router-link to="/news" class="fd-nav-link">📰 اخبار فوتبال</router-link>
          </div>
        </article>

        <!-- سایدبار: بایگانی -->
        <FactArchive
            class="fd-aside"
            :facts="archiveFacts"
            :current-slug="fact.slug"
        />
      </div>
    </template>

    <div v-else class="fd-loading">
      <span class="fd-spinner" aria-hidden="true"></span>
      <p>در حال بارگذاری دانستنی…</p>
    </div>
  </div>
</template>

<script>
import { setSeo } from '@/utils/seo'
import api from '@/services/api'
import FactArchive from '@/components/FactArchive.vue'

export default {
  name: 'FactDetail',

  components: { FactArchive },

  data() {
    return {
      fact: null,
      archiveFacts: [],
      error: false,
    }
  },

  computed: {
    showDate() {
      return this.fact && (this.fact.publishedAt || this.fact.featuredAt)
    },
  },

  created() {
    this.loadFact()
  },

  methods: {
    async loadFact() {
      const slug = this.$route.params.slug
      this.fact = null
      this.error = false
      try {
        const f = await api.getFact(slug)
        if (f && f.published) {
          this.fact = f
          setSeo({
            title: `${f.title} | دانستنی‌های فوتبالی | ضربه آزاد`,
            description: (f.paragraphs && f.paragraphs[0] || f.title).slice(0, 160),
            path: '/did-you-know/' + encodeURIComponent(f.slug),
            image: f.imageUrl || undefined,
          })
          this.loadArchive()
        } else {
          // حذف‌شده/ناموجود (404 واقعی بک‌اند) → noindex تا Soft-404 نشود؛
          // خطای سرور/شبکه هرگز noindex نمی‌گیرد
          setSeo({ notFound: api.lastStatus === 404 })
          this.error = true
        }
      } catch (e) {
        setSeo({ notFound: api.lastStatus === 404 })
        this.error = true
      }
    },

    // بایگانی کنار مطلب — همان سرویس عمومی؛ فکت جاری حذف می‌شود
    async loadArchive() {
      try {
        const list = await api.getFacts()
        if (Array.isArray(list)) {
          this.archiveFacts = list
              .filter((x) => x.slug !== this.fact.slug)
              .sort((a, b) =>
                  new Date(b.featuredAt || b.publishedAt || 0) -
                  new Date(a.featuredAt || a.publishedAt || 0))
              .slice(0, 30)
        }
      } catch { /* بایگانی اختیاری است */ }
    },

    faDate(d) {
      try {
        return new Date(d).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' })
      } catch { return '' }
    },
  },

  watch: {
    '$route.params.slug'() {
      this.loadFact()
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/sass/variables';
@import '@/assets/sass/mixin';

.fd-page {
  margin: 0 auto;
  padding: 24px 12px 48px;
}

.fd-crumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: #9ca3af;
  margin-bottom: 14px;

  a {
    color: #047857;
    text-decoration: none;
    font-weight: 700;

    &:hover { text-decoration: underline; }
  }
}

.fd-crumbs-here {
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

.fd-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 20px;
  align-items: start;
}

.fd-aside { min-width: 0; }

.fd-article {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 18px;
  padding: 26px;
  box-shadow: $shadow-sm;
}

.fd-head {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.fd-badge {
  display: inline-block;
  background: linear-gradient(135deg, #064e3b, #065f46);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 999px;
}

.fd-cat {
  display: inline-block;
  background: linear-gradient(135deg, #047857, #10b981);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 999px;
}

.fd-title {
  margin: 0 0 10px;
  font-size: 23px;
  line-height: 1.7;
  color: #111827;
}

.fd-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding-bottom: 14px;
  border-bottom: 1px dashed $gray-200;
  margin-bottom: 16px;
}

.fd-date {
  color: #6b7280;
  font-size: 13px;
}

.fd-source {
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover { text-decoration: underline; }
}

.fd-photo {
  margin: 0 0 18px;
  border-radius: 14px;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    max-height: 420px;
    object-fit: cover;
  }
}

.fd-figure {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: linear-gradient(160deg, #fef2f2, #fff7ed);
  border: 1px solid #fecaca;
  border-radius: 14px;
  padding: 12px 22px;
  margin-bottom: 18px;

  .fd-figure-num {
    font-size: 28px;
    font-weight: 900;
    color: $red;
  }

  .fd-figure-label {
    font-size: 12px;
    font-weight: 700;
    color: #6b7280;
    text-align: center;
  }
}

.fd-body {
  p {
    margin: 0 0 12px;
    color: #374151;
    font-size: 15px;
    line-height: 2.1;
    text-align: justify;

    &:last-child { margin-bottom: 0; }
  }
}

.fd-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 18px;
}

.fd-tag {
  background: $gray-50;
  border: 1px solid $gray-200;
  color: #6b7280;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 11px;
  border-radius: 999px;
}

.fd-verify {
  margin-top: 16px;
  border-top: 1px dashed $gray-300;
  padding-top: 10px;

  summary {
    cursor: pointer;
    color: #1e3a8a;
    font-size: 13px;
    font-weight: 700;
    user-select: none;

    &:hover { color: #3b82f6; }
  }

  p {
    margin: 8px 0 2px;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.95;
    text-align: justify;
  }
}

.fd-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px dashed $gray-200;
}

.fd-nav-link {
  color: #1e3a8a;
  font-weight: 700;
  font-size: 13.5px;
  text-decoration: none;
  padding: 8px 14px;
  border-radius: 10px;
  background: $gray-50;

  &:hover { background: $gray-100; }
}

// ---------- حالت‌های خالی/لودینگ ----------
.fd-error {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 18px;

  .fd-error-icon { font-size: 44px; }

  h1 {
    font-size: 20px;
    color: #111827;
    margin: 10px 0 6px;
  }

  p {
    color: #6b7280;
    font-size: 14px;
    margin: 0 0 16px;
  }
}

.fd-back {
  color: #047857;
  font-weight: 800;
  text-decoration: none;

  &:hover { text-decoration: underline; }
}

.fd-loading {
  text-align: center;
  padding: 60px 0;
  color: #9ca3af;

  .fd-spinner {
    display: inline-block;
    width: 34px;
    height: 34px;
    border: 3px solid $gray-200;
    border-top-color: #047857;
    border-radius: 50%;
    animation: fdspin .8s linear infinite;
    margin-bottom: 10px;
  }
}

@keyframes fdspin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .fd-page { padding: 12px 8px 36px; }

  .fd-layout { grid-template-columns: 1fr; }

  .fd-aside { order: 2; }

  .fd-article {
    padding: 16px 14px;
    border-radius: 14px;
  }

  .fd-title { font-size: 19px; }

  .fd-photo img { max-height: 240px; }

  .fd-body p { font-size: 14px; }
}
</style>
